/**
 * Active Agent Context - Shared state for current focused entity
 *
 * Stores the currently active/focused entity across Chat and Dashboard layers.
 * Both AgentChatConsole and AgentShell/custom grids can read and update this state.
 *
 * USAGE:
 * ```tsx
 * // In a panel component
 * const { active, setActive } = useActiveAgentContext()
 *
 * const handleRowClick = (leadId: string) => {
 *   setActive({ agent: 'booking', kind: 'lead', id: leadId })
 * }
 *
 * // In chat or shell
 * const { active } = useActiveAgentContext()
 * if (active) {
 *   console.log(`Current focus: ${active.kind} ${active.id} from ${active.agent}`)
 * }
 * ```
 */
import { createContext, useContext, useState, type ReactNode } from 'react'
import type { EntityType } from '@/lib/agentUiBridge'

/**
 * Active entity state
 */
export interface ActiveEntity {
  /** Agent that owns this entity */
  agent: string
  /** Entity type */
  kind: EntityType
  /** Entity ID */
  id: string
  /** Optional metadata */
  metadata?: Record<string, any>
}

/**
 * Context value interface
 */
interface ActiveAgentContextValue {
  /** Currently active entity (null if none) */
  active: ActiveEntity | null
  /** Set the active entity */
  setActive: (entity: ActiveEntity | null) => void
  /** Clear the active entity */
  clearActive: () => void
}

/**
 * Context instance
 */
const ActiveAgentContext = createContext<ActiveAgentContextValue | null>(null)

/**
 * Provider props
 */
interface ActiveAgentContextProviderProps {
  children: ReactNode
}

/**
 * Active Agent Context Provider
 */
export function ActiveAgentContextProvider({ children }: ActiveAgentContextProviderProps) {
  const [active, setActive] = useState<ActiveEntity | null>(null)

  const clearActive = () => setActive(null)

  return (
    <ActiveAgentContext.Provider value={{ active, setActive, clearActive }}>
      {children}
    </ActiveAgentContext.Provider>
  )
}

/**
 * Hook to access active agent context
 */
export function useActiveAgentContext(): ActiveAgentContextValue {
  const context = useContext(ActiveAgentContext)
  if (!context) {
    throw new Error('useActiveAgentContext must be used within ActiveAgentContextProvider')
  }
  return context
}
